import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Breadcrumbs,
    Chip,
    CircularProgress,
    Pagination,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import { ProjectContext } from '../../../../providers/projectProvider';
import {
    getEvaluatorDashboardSummary,
    getEvaluatorExamCandidates,
} from '../../../../services/evaluatorDashboardService';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';

import './EvaluatorDashboard.scss';

const PAGE_LIMIT = parseInt(process.env.REACT_APP_PAGE_LIMIT) || 10;

// ─── Icons ────────────────────────────────────────────────────────────────────

const CARD_ICONS = {
    assigned: (
        <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'>
            <line x1='8' y1='6' x2='21' y2='6' /><line x1='8' y1='12' x2='21' y2='12' /><line x1='8' y1='18' x2='21' y2='18' />
            <circle cx='3.5' cy='6' r='1.2' fill='currentColor' stroke='none' />
            <circle cx='3.5' cy='12' r='1.2' fill='currentColor' stroke='none' />
            <circle cx='3.5' cy='18' r='1.2' fill='currentColor' stroke='none' />
        </svg>
    ),
    clock: (
        <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'>
            <circle cx='12' cy='12' r='10' /><polyline points='12 6 12 12 16 14' />
        </svg>
    ),
    check: (
        <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'>
            <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' /><polyline points='22 4 12 14.01 9 11.01' />
        </svg>
    ),
    progress: (
        <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'>
            <polyline points='23 6 13.5 15.5 8.5 10.5 1 18' />
            <polyline points='17 6 23 6 23 12' />
        </svg>
    ),
};

// ─── Summary card ─────────────────────────────────────────────────────────────

const DashboardCard = ({ title, count, iconKey, iconColor, iconBg, accent }) => (
    <Box sx={{
        flex: '1 1 160px',
        minWidth: 0,
        maxWidth: '100%',
        bgcolor: '#fff',
        borderRadius: 2,
        boxShadow: '0 1px 5px rgba(0,0,0,0.07)',
        overflow: 'hidden',
        borderLeft: `4px solid ${accent}`,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{
                fontSize: 10,
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: 0.8,
            }}>
                {title}
            </Typography>
            <Box sx={{
                width: 34, height: 34,
                borderRadius: '50%',
                bgcolor: iconBg,
                color: iconColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                {CARD_ICONS[iconKey]}
            </Box>
        </Box>
        <Typography sx={{
            fontSize: 30,
            fontWeight: 800,
            color: '#1e293b',
            lineHeight: 1,
            letterSpacing: -1,
        }}>
            {count ?? 0}
        </Typography>
    </Box>
);

// ─── Exam workload card ───────────────────────────────────────────────────────

const ExamWorkloadCard = ({ exam, selected, onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            p: 2,
            bgcolor: selected ? '#4f46e5' : '#fff',
            border: '1.5px solid',
            borderColor: selected ? '#4f46e5' : '#e2e8f0',
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            '&:hover': {
                borderColor: selected ? '#4f46e5' : '#a5b4fc',
                boxShadow: '0 3px 10px rgba(99,102,241,0.15)',
                transform: 'translateY(-1px)',
            },
        }}
    >
        <Typography sx={{
            fontSize: 12,
            fontWeight: 700,
            mb: 1.5,
            color: selected ? '#fff' : '#1e293b',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }}>
            {exam.courseName}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {[
                { label: 'Pending',     value: exam.pendingCandidates,    activeColor: '#fbbf24', color: '#d97706' },
                { label: 'In Progress', value: exam.inProgressCandidates, activeColor: '#a5b4fc', color: '#6366f1' },
                { label: 'Completed',   value: exam.completedCandidates,  activeColor: '#86efac', color: '#16a34a' },
            ].map(({ label, value, activeColor, color }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ fontSize: 10, color: selected ? 'rgba(255,255,255,0.65)' : '#94a3b8' }}>
                        {label}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: selected ? activeColor : color }}>
                        {value ?? 0}
                    </Typography>
                </Box>
            ))}
        </Box>
    </Box>
);

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_FILTERS = ['ALL', 'PENDING', 'IN_PROGRESS', 'COMPLETED'];

const statusChipProps = {
    PENDING:     { bgcolor: '#FEF9C3', color: '#854D0E', border: '1px solid #FDE68A' },
    IN_PROGRESS: { bgcolor: '#EDE9FE', color: '#5B21B6', border: '1px solid #DDD6FE' },
    COMPLETED:   { bgcolor: '#DCFCE7', color: '#166534', border: '1px solid #BBF7D0' },
};

// ─── Table header columns ────────────────────────────────────────────────────

const TABLE_COLS = ['Candidate Id', 'Exam', 'Submitted At', 'Status', 'Action'];

// ─── Main component ───────────────────────────────────────────────────────────

export const EvaluatorDashboardLayout = () => {
    const params = useParams();
    const projectName = params?.projectName;
    const navigate = useNavigate();
    const { projectDetails } = useContext(ProjectContext);

    const [dashboardData, setDashboardData] = useState(null);
    const [selectedExam, setSelectedExam] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState('ALL');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loadingDashboard, setLoadingDashboard] = useState(true);
    const [loadingCandidates, setLoadingCandidates] = useState(false);

    // ── Fetch dashboard summary on mount ───────────────────────────────────────
    useEffect(() => {
        const fetchDashboard = async () => {
            setLoadingDashboard(true);
            try {
                const res = await getEvaluatorDashboardSummary();
                if (res.code === 'OK') {
                    setDashboardData(res.data);
                    if (res.data.evaluatorExams?.length) {
                        setSelectedExam(res.data.evaluatorExams[0]);
                    }
                }
            } catch (err) {
                console.error('Error fetching evaluator dashboard', err);
            } finally {
                setLoadingDashboard(false);
            }
        };
        fetchDashboard();
    }, []);

    // ── Fetch candidates when exam / status / page changes ─────────────────────
    useEffect(() => {
        if (!selectedExam) return;
        const fetchCandidates = async () => {
            setLoadingCandidates(true);
            try {
                const statusParam = selectedStatus === 'ALL' ? undefined : selectedStatus;
                const res = await getEvaluatorExamCandidates({
                    projectMasterId: selectedExam.projectMasterId,
                    status: statusParam,
                    page,
                    limit: PAGE_LIMIT,
                });
                if (res.code === 'OK') {
                    setCandidates(res.data.candidates ?? []);
                    setTotalPages(res.data.totalPages ?? 1);
                    setTotalRecords(res.data.totalRecords ?? 0);
                }
            } catch (err) {
                console.error('Error fetching evaluator candidates', err);
            } finally {
                setLoadingCandidates(false);
            }
        };
        fetchCandidates();
    }, [selectedExam, selectedStatus, page]);

    const handleExamClick = (exam) => {
        setSelectedExam(exam);
        setPage(1);
        setSelectedStatus('ALL');
    };

    const handleStatusFilter = (status) => {
        setSelectedStatus(status);
        setPage(1);
    };

    const handleOpenCandidate = (evaluatorCandidateHashId) => {
        navigate(`/${(projectName)}/evaluation/candidates/${(evaluatorCandidateHashId)}`);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const summary = dashboardData?.summary ?? {};
    const evaluatorExams = dashboardData?.evaluatorExams ?? [];

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label='breadcrumb' separator='' sx={{ gap: 12 }}>
                    <Typography color='text.primary' sx={{ fontWeight: 400, textTransform: 'capitalize' }}>
                        {projectDetails?.projectName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt='' />
                    </Box>
                    <Typography color='text.primary' sx={{ fontWeight: 500 }}>
                        Evaluator Dashboard
                    </Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' />
                    </Box>
                </Breadcrumbs>
            </Menu>

            <Container>
                {/* ── Page header ── */}
                <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                        Evaluator Dashboard
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#64748b', mt: 0.5 }}>
                        Review assigned candidates and start evaluations
                    </Typography>
                </Box>

                {loadingDashboard ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10, gap: 2 }}>
                        <CircularProgress size={28} thickness={4} />
                        <Typography sx={{ color: '#64748b', fontSize: 14 }}>Loading dashboard…</Typography>
                    </Box>
                ) : (
                    <>
                        {/* ── Summary Cards ── */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, width: '100%', minWidth: 0 }}>
                            <DashboardCard
                                title='Total Assigned'
                                count={summary.totalAssignedCandidates}
                                iconKey='assigned'
                                iconColor='#6B7280'
                                iconBg='#F1F5F9'
                                accent='#94a3b8'
                            />
                            <DashboardCard
                                title='Pending'
                                count={summary.totalPendingCandidates}
                                iconKey='clock'
                                iconColor='#D97706'
                                iconBg='#FEF3C7'
                                accent='#F59E0B'
                            />
                            <DashboardCard
                                title='In Progress'
                                count={summary.totalInProgressCandidates}
                                iconKey='progress'
                                iconColor='#7C3AED'
                                iconBg='#EDE9FE'
                                accent='#7C3AED'
                            />
                            <DashboardCard
                                title='Completed'
                                count={summary.totalCompletedCandidates}
                                iconKey='check'
                                iconColor='#059669'
                                iconBg='#D1FAE5'
                                accent='#10B981'
                            />
                        </Box>

                        {/* ── Workload by Exam ── */}
                        {evaluatorExams.length > 0 && (
                            <Box sx={{
                                bgcolor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 2,
                                p: 2,
                                mb: 2.5,
                                width: '100%',
                                minWidth: 0,
                                overflow: 'hidden',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
                                        Workload by Exam
                                    </Typography>
                                    <Typography sx={{ ml: 1, fontSize: 11, color: '#94a3b8' }}>
                                        ({evaluatorExams.length} exam{evaluatorExams.length !== 1 ? 's' : ''})
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    width: '100%',
                                    overflowX: 'auto',
                                    pb: 1,
                                    scrollbarWidth: 'thin',
                                    '&::-webkit-scrollbar': { height: 4 },
                                    '&::-webkit-scrollbar-track': { background: '#f1f5f9', borderRadius: 2 },
                                    '&::-webkit-scrollbar-thumb': { background: '#c7d2fe', borderRadius: 2 },
                                    '&::-webkit-scrollbar-thumb:hover': { background: '#6366f1' },
                                }}>
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        {evaluatorExams.map((exam) => (
                                            <Box key={exam.projectMasterId} sx={{ minWidth: 190, maxWidth: 240, flexShrink: 0 }}>
                                                <ExamWorkloadCard
                                                    exam={exam}
                                                    selected={selectedExam?.projectMasterId === exam.projectMasterId}
                                                    onClick={() => handleExamClick(exam)}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* ── Candidate Table ── */}
                        {selectedExam && (
                            <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
                                {/* Section header */}
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
                                            {selectedExam.courseName}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.25 }}>
                                            {totalRecords} candidate{totalRecords !== 1 ? 's' : ''}
                                        </Typography>
                                    </Box>

                                    {/* Status filter pills */}
                                    <Box sx={{ display: 'flex', gap: 0.75, ml: 'auto', flexWrap: 'wrap' }}>
                                        {STATUS_FILTERS.map((s) => (
                                            <Box
                                                key={s}
                                                component='button'
                                                onClick={() => handleStatusFilter(s)}
                                                sx={{
                                                    px: 1.5, py: 0.5,
                                                    borderRadius: 5,
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    border: '1.5px solid',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.12s',
                                                    ...(selectedStatus === s
                                                        ? { bgcolor: '#4f46e5', borderColor: '#4f46e5', color: '#fff' }
                                                        : { bgcolor: '#fff', borderColor: '#e2e8f0', color: '#64748b',
                                                            '&:hover': { borderColor: '#a5b4fc', color: '#4f46e5' } }
                                                    ),
                                                }}
                                            >
                                                {s.replace('_', ' ')}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>

                                {/* Table */}
                                <Paper elevation={0} sx={{
                                    width: '100%',
                                    overflow: 'hidden',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 2,
                                    mb: 2,
                                }}>
                                    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                                        <Table aria-label='candidates table'>
                                            <TableHead>
                                                <TableRow>
                                                    {TABLE_COLS.map((col) => (
                                                        <TableCell
                                                            key={col}
                                                            align='center'
                                                            sx={{
                                                                bgcolor: '#f8fafc',
                                                                color: '#374151',
                                                                fontWeight: 700,
                                                                fontSize: 11,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: 0.6,
                                                                borderBottom: '2px solid #e2e8f0',
                                                                px: 2, py: 1.5,
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {col}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {loadingCandidates ? (
                                                    <TableRow>
                                                        <TableCell colSpan={5} sx={{ border: 'none', textAlign: 'center', py: 5 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5 }}>
                                                                <CircularProgress size={20} thickness={4} />
                                                                <Typography sx={{ color: '#94a3b8', fontSize: 13 }}>Loading candidates…</Typography>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : !candidates.length ? (
                                                    <TableRow>
                                                        <TableCell colSpan={5} sx={{ border: 'none', textAlign: 'center', py: 6 }}>
                                                            <Typography sx={{ color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>
                                                                No candidates found
                                                            </Typography>
                                                            <Typography sx={{ color: '#cbd5e1', fontSize: 12, mt: 0.5 }}>
                                                                Try a different filter or check back later
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    candidates.map((c, idx) => (
                                                        <TableRow
                                                            key={idx}
                                                            sx={{
                                                                '&:hover': { bgcolor: '#f5f3ff' },
                                                                '&:last-child td': { border: 'none' },
                                                                transition: 'background-color 0.1s',
                                                            }}
                                                        >
                                                            <TableCell align='center' sx={{ px: 2, py: 1.25, fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>
                                                                {c.candidateHashId}
                                                            </TableCell>
                                                            <TableCell align='center' sx={{ px: 2, py: 1.25, fontSize: 13, color: '#1e293b', fontWeight: 500 }}>
                                                                {selectedExam.courseName}
                                                            </TableCell>
                                                            <TableCell align='center' sx={{ px: 2, py: 1.25, fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                                                                {formatDate(c.submittedAt)}
                                                            </TableCell>
                                                            <TableCell align='center' sx={{ px: 2, py: 1.25 }}>
                                                                <Chip
                                                                    label={c.status?.replace('_', ' ')}
                                                                    size='small'
                                                                    sx={{
                                                                        fontWeight: 700,
                                                                        fontSize: 11,
                                                                        height: 22,
                                                                        borderRadius: 1,
                                                                        ...(statusChipProps[c.status] ?? {}),
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align='center' sx={{ px: 2, py: 1.25 }}>
                                                                <Box
                                                                    component='button'
                                                                    onClick={() => handleOpenCandidate(c.evaluatorCandidateHashId)}
                                                                    title='Start evaluation'
                                                                    sx={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: 0.5,
                                                                        px: 1.5, py: 0.5,
                                                                        borderRadius: 1.5,
                                                                        border: '1.5px solid #e0e7ff',
                                                                        bgcolor: '#f5f3ff',
                                                                        color: '#4f46e5',
                                                                        fontSize: 11,
                                                                        fontWeight: 700,
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.12s',
                                                                        '&:hover': {
                                                                            bgcolor: '#4f46e5',
                                                                            borderColor: '#4f46e5',
                                                                            color: '#fff',
                                                                        },
                                                                    }}
                                                                >
                                                                    Evaluate
                                                                    <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                                                                        <polyline points='9 18 15 12 9 6' />
                                                                    </svg>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Pagination
                                            count={totalPages}
                                            page={page}
                                            onChange={(_, v) => setPage(v)}
                                            shape='rounded'
                                            size='small'
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* ── Empty state ── */}
                        {!evaluatorExams.length && !loadingDashboard && (
                            <Box sx={{
                                textAlign: 'center',
                                py: 10,
                                border: '1.5px dashed #e2e8f0',
                                borderRadius: 2,
                                bgcolor: '#fafafa',
                            }}>
                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#374151', mb: 0.75 }}>
                                    No exams assigned yet
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>
                                    Check back after your exam assignments are configured.
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Container>
        </MainContainer>
    );
};
